/* VANDAL — silnik „wisior z Twoim wzorem” (przeglądarka + Node).
 * Wersja 0.4 (21.09.2026). Działa na surowych pikselach RGBA, bez bibliotek.
 * 0.4: szybsza transformata odległości (te same wyniki), bez „przechyłu”, pusty wzór = werdykt zamiast błędu.
 *      Konfigurator woła run(..., {contours: false}) — DXF robi warsztat ręcznie z pliku klienta.
 *
 * Kolejność: plik → mapa atramentu → skala mm → porządki → łączenie liter → wypełnienie
 *            → najsłabsze połączenie → korekta → mocowanie → werdykt → kontury/DXF
 *
 * Liczby z warsztatu (Mateusz / DXF Cuzi): blacha 2 mm, laser ±0,1 mm, oczko Ø 2,05 mm,
 * ścianka oczka ~0,85 mm. Próg połączenia 0,6 mm: kalibracja na SOUL BASICS (35 mm, 0,65 mm).
 */
(function (root) {
  "use strict";
  const CFG = {
    sheetMM: 2.0,
    minLineMM: 0.3,      // z tolerancji ±0,1 mm
    minGapMM: 0.3,
    bridgeOkMM: 0.6,     // SOUL BASICS: 0,65 mm — wycięte i noszone
    bridgeMinMM: 0.3,    // poniżej: „na nitce” (k41core)
    detailMM2: 1.0,      // plamki < 1 mm² = szum
    speckReportMM2: 0.15, // …ale od 0,15 mm² mówimy klientowi, że coś pominęliśmy
    smallFrac: 0.06,     // element < 6% największego = „drobny”
    smallTextMin: 3,     // ≥3 drobne elementy w linii = drobny napis → usuwamy
    overlapMinMM: 0.5,   // minimalna zakładka przy nasuwaniu liter
    overlapStroke: 0.3,  // zakładka = 30% grubości kreski
    eyeHoleMM: 2.05, eyeWallMM: 0.85, eyeOverlapMM: 0.7,
    bailWMM: 3.0, bailHMM: 7.0,
    marginMM: 8,
    maxSrcPx: 1600,
  };

  // ------------------------------------------------------------------ bitmapy
  const mk = (w, h) => ({ w, h, d: new Uint8Array(w * h) });
  const clone = (m) => ({ w: m.w, h: m.h, d: m.d.slice() });
  const count = (m) => { let c = 0; for (let i = 0; i < m.d.length; i++) c += m.d[i]; return c; };
  const or = (a, b) => { const o = clone(a); for (let i = 0; i < o.d.length; i++) o.d[i] |= b.d[i]; return o; };
  const andNot = (a, b) => { const o = clone(a); for (let i = 0; i < o.d.length; i++) o.d[i] = a.d[i] & (1 - b.d[i]); return o; };

  // Felzenszwalb–Huttenlocher: kwadrat odległości euklidesowej
  const INF = 1e20;
  function edt1d(f, n, d, v, z) {
    let k = 0; v[0] = 0; z[0] = -INF; z[1] = INF;
    for (let q = 1; q < n; q++) {
      let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      while (s <= z[k]) { k--; s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]); }
      k++; v[k] = q; z[k] = s; z[k + 1] = INF;
    }
    k = 0;
    for (let q = 0; q < n; q++) { while (z[k + 1] < q) k++; d[q] = (q - v[k]) * (q - v[k]) + f[v[k]]; }
  }
  // odległość każdego piksela do najbliższego piksela, gdzie m == target.
  // v0.4: maska jest binarna, więc przebieg w pionie to dwa zwykłe skany (bez paraboli), a oba przebiegi
  // idą wierszami po ciągłej pamięci. Wynik ten sam co w v0.3 (sprawdzone bit w bit na plikach testowych).
  function dist(m, target) {
    const { w, h } = m, md = m.d;
    const g = new Float64Array(w * h), f = new Float64Array(w), d = new Float64Array(w);
    const v = new Int32Array(w), z = new Float64Array(w + 1), last = new Int32Array(w).fill(-1);
    for (let y = 0, o = 0; y < h; y++, o += w) for (let x = 0; x < w; x++) {
      if (md[o + x] === target) last[x] = y;
      const l = last[x]; g[o + x] = l < 0 ? INF : (y - l) * (y - l);
    }
    last.fill(-1);
    for (let y = h - 1, o = (h - 1) * w; y >= 0; y--, o -= w) for (let x = 0; x < w; x++) {
      if (md[o + x] === target) { last[x] = y; continue; }
      const l = last[x]; if (l >= 0) { const q = (l - y) * (l - y); if (q < g[o + x]) g[o + x] = q; }
    }
    for (let y = 0, o = 0; y < h; y++, o += w) {
      for (let x = 0; x < w; x++) f[x] = g[o + x];
      edt1d(f, w, d, v, z);
      for (let x = 0; x < w; x++) g[o + x] = Math.sqrt(d[x]);
    }
    return g;
  }
  // wycinek maski (x0..x1, y0..y1 włącznie)
  function sub(m, x0, y0, x1, y1) {
    const W = x1 - x0 + 1, H = y1 - y0 + 1, o = mk(W, H);
    for (let y = 0; y < H; y++) o.d.set(m.d.subarray((y + y0) * m.w + x0, (y + y0) * m.w + x0 + W), y * W);
    return o;
  }
  // v0.4: erozja i dylatacja liczą odległości tylko w obrysie wzoru (+ zapas), a nie na całym arkuszu z marginesami.
  // Poza obrysem wynik jest z góry znany (0), a w środku — identyczny jak na całym arkuszu.
  function morph(m, r, grow) {
    const o = mk(m.w, m.h), b = bbox(m); if (b.x1 < 0) return o;
    const P = grow ? Math.ceil(r) + 1 : 1;
    const x0 = Math.max(0, b.x0 - P), y0 = Math.max(0, b.y0 - P), x1 = Math.min(m.w - 1, b.x1 + P), y1 = Math.min(m.h - 1, b.y1 + P);
    const s = sub(m, x0, y0, x1, y1), g = dist(s, grow ? 1 : 0);
    for (let y = 0; y < s.h; y++) {
      const so = y * s.w, oo = (y + y0) * m.w + x0;
      if (grow) for (let x = 0; x < s.w; x++) o.d[oo + x] = g[so + x] <= r ? 1 : 0;
      else for (let x = 0; x < s.w; x++) o.d[oo + x] = g[so + x] > r ? 1 : 0;
    }
    return o;
  }
  const erode = (m, r) => morph(m, r, false);
  const dilate = (m, r) => morph(m, r, true);
  const open = (m, r) => dilate(erode(m, r), r);

  // składowe spójne (8 dla metalu, 4 dla tła)
  function label(m, conn8 = true, val = 1) {
    const { w, h } = m, lab = new Int32Array(w * h), st = new Int32Array(w * h);
    const comps = [null];
    let n = 0;
    for (let s = 0; s < w * h; s++) {
      if (m.d[s] !== val || lab[s]) continue;
      n++; let top = 0; st[top++] = s; lab[s] = n;
      let area = 0, x0 = w, y0 = h, x1 = 0, y1 = 0, sx = 0, sy = 0, border = false;
      while (top) {
        const p = st[--top], x = p % w, y = (p / w) | 0;
        area++; sx += x; sy += y;
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
        if (x === 0 || y === 0 || x === w - 1 || y === h - 1) border = true;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          if (!conn8 && dx && dy) continue;
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const q = ny * w + nx;
          if (m.d[q] === val && !lab[q]) { lab[q] = n; st[top++] = q; }
        }
      }
      comps.push({ id: n, area, x0, y0, x1, y1, cx: sx / area, cy: sy / area, border });
    }
    return { lab, comps, n };
  }
  const holesOf = (m) => {
    const L = label(m, false, 0), o = mk(m.w, m.h);
    const inner = new Uint8Array(L.n + 1);
    for (let i = 1; i <= L.n; i++) inner[i] = L.comps[i].border ? 0 : 1;
    for (let i = 0; i < o.d.length; i++) o.d[i] = inner[L.lab[i]];
    return o;
  };

  // ------------------------------------------------------------------ 1. mapa atramentu
  function otsu(vals) {
    const hist = new Float64Array(256); let n = 0;
    for (const v of vals) { hist[Math.max(0, Math.min(255, v | 0))]++; n++; }
    let sum = 0; for (let i = 0; i < 256; i++) sum += i * hist[i];
    let sB = 0, wB = 0, best = 0, t = 127;
    for (let i = 0; i < 256; i++) {
      wB += hist[i]; if (!wB) continue; const wF = n - wB; if (!wF) break;
      sB += i * hist[i]; const mB = sB / wB, mF = (sum - sB) / wF;
      const b = wB * wF * (mB - mF) * (mB - mF); if (b > best) { best = b; t = i; }
    }
    return t;
  }
  function inkMap(img, opt = {}) {
    // img: {width, height, data: RGBA}
    let { width: w, height: h, data } = img;
    const diag = {};
    let transparent = 0;
    for (let i = 3; i < data.length; i += 4) if (data[i] < 128) transparent++;
    const ink = new Float32Array(w * h);
    if (transparent / (w * h) > 0.03) {
      for (let i = 0; i < w * h; i++) ink[i] = data[i * 4 + 3] / 255;
      if (opt.invert) for (let i = 0; i < ink.length; i++) ink[i] = 1 - ink[i];
      diag.zrodlo = "przezroczystosc"; diag.typ = "grafika";
      return { ink, w, h, diag };
    }
    const g = new Float32Array(w * h);
    let hueHist = new Float64Array(12), colored = 0;
    for (let i = 0; i < w * h; i++) {
      const r = data[i * 4], gg = data[i * 4 + 1], b = data[i * 4 + 2];
      g[i] = 0.299 * r + 0.587 * gg + 0.114 * b;
      const mx = Math.max(r, gg, b), mn = Math.min(r, gg, b);
      if (mx > 60 && (mx - mn) / (mx || 1) > 0.32) {
        colored++;
        let hh;
        if (mx === r) hh = ((gg - b) / (mx - mn)) % 6; else if (mx === gg) hh = (b - r) / (mx - mn) + 2; else hh = (r - gg) / (mx - mn) + 4;
        hh = (hh * 60 + 360) % 360; hueHist[Math.min(11, (hh / 30) | 0)]++;
      }
    }
    // polaryzacja: tło = mediana obrzeża
    const b = Math.max(2, Math.round(0.03 * Math.min(w, h))), border = [];
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (x < b || y < b || x >= w - b || y >= h - b) border.push(g[y * w + x]);
    border.sort((a, c) => a - c);
    let lightBg = border[border.length >> 1] > 127;
    if (opt.invert) lightBg = !lightBg;
    if (!lightBg) for (let i = 0; i < g.length; i++) g[i] = 255 - g[i];      // teraz: ciemne = atrament
    // tło szacowane maksimum w blokach (papier jest jasny) + rozmycie
    let extreme = 0; for (let i = 0; i < g.length; i++) if (g[i] < 50 || g[i] > 205) extreme++;
    extreme /= g.length;
    let use = g;
    if (extreme <= 0.85) {
      const B = Math.max(8, Math.round(Math.max(w, h) / 24)), gw = Math.ceil(w / B), gh = Math.ceil(h / B);
      let grid = new Float32Array(gw * gh);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { const k = ((y / B) | 0) * gw + ((x / B) | 0); if (g[y * w + x] > grid[k]) grid[k] = g[y * w + x]; }
      for (let pass = 0; pass < 2; pass++) {
        const o = new Float32Array(gw * gh);
        for (let y = 0; y < gh; y++) for (let x = 0; x < gw; x++) {
          let s = 0, c = 0; for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) { const X = x + dx, Y = y + dy; if (X >= 0 && Y >= 0 && X < gw && Y < gh) { s += grid[Y * gw + X]; c++; } }
          o[y * gw + x] = s / c;
        }
        grid = o;
      }
      use = new Float32Array(w * h);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const fx = Math.min(gw - 1.001, Math.max(0, x / B - 0.5)), fy = Math.min(gh - 1.001, Math.max(0, y / B - 0.5));
        const x0 = fx | 0, y0 = fy | 0, ax = fx - x0, ay = fy - y0;
        const x1 = Math.min(gw - 1, x0 + 1), y1 = Math.min(gh - 1, y0 + 1);
        const bg = (grid[y0 * gw + x0] * (1 - ax) + grid[y0 * gw + x1] * ax) * (1 - ay) + (grid[y1 * gw + x0] * (1 - ax) + grid[y1 * gw + x1] * ax) * ay;
        use[y * w + x] = Math.min(255, g[y * w + x] / Math.max(bg, 1) * 255);
      }
    }
    let t = otsu(use) * (1 + 0.25 * (opt.sensitivity || 0));
    let far = 0;
    for (let i = 0; i < use.length; i++) { ink[i] = Math.max(0, Math.min(1, (t - use[i]) / 24 + 0.5)); if (Math.abs(use[i] - t) > 45) far++; }
    const nh = colored > 200 ? Array.from(hueHist).filter((v) => v / colored > 0.08).length : 0;
    diag.czystosc = +(far / use.length).toFixed(2); diag.barw = nh;
    diag.typ = diag.czystosc >= 0.9 && nh < 3 ? "grafika" : "zdjecie";
    return { ink, w, h, diag };
  }

  // ------------------------------------------------------------------ 2. skala
  function toWork(im, sizeMM, ppm) {
    const { ink, w, h } = im;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (ink[y * w + x] > 0.5) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    if (x1 < 0) return null;
    const cw = x1 - x0 + 1, ch = y1 - y0 + 1, longSrc = Math.max(cw, ch);
    const s = sizeMM * ppm / longSrc;
    const W = Math.max(1, Math.round(cw * s)), H = Math.max(1, Math.round(ch * s));
    const M = Math.round(CFG.marginMM * ppm);
    const m = mk(W + 2 * M, H + 2 * M);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const fx = Math.min(cw - 1.001, Math.max(0, (x + 0.5) / s - 0.5)) + x0, fy = Math.min(ch - 1.001, Math.max(0, (y + 0.5) / s - 0.5)) + y0;
      const xa = fx | 0, ya = fy | 0, ax = fx - xa, ay = fy - ya, xb = Math.min(w - 1, xa + 1), yb = Math.min(h - 1, ya + 1);
      const v = (ink[ya * w + xa] * (1 - ax) + ink[ya * w + xb] * ax) * (1 - ay) + (ink[yb * w + xa] * (1 - ax) + ink[yb * w + xb] * ax) * ay;
      m.d[(y + M) * m.w + x + M] = v > 0.5 ? 1 : 0;
    }
    return { m, srcPxPerMM: +(longSrc / sizeMM).toFixed(1) };
  }

  // wycina jedną składową jako osobną bitmapę
  function extract(m, L, id) { const o = mk(m.w, m.h); for (let i = 0; i < o.d.length; i++) o.d[i] = L.lab[i] === id ? 1 : 0; return o; }
  function shift(m, dx, dy) {
    const o = mk(m.w, m.h);
    for (let y = 0; y < m.h; y++) { const Y = y + dy; if (Y < 0 || Y >= m.h) continue; for (let x = 0; x < m.w; x++) { if (!m.d[y * m.w + x]) continue; const X = x + dx; if (X >= 0 && X < m.w) o.d[Y * m.w + X] = 1; } }
    return o;
  }
  function bbox(m) { let x0 = m.w, y0 = m.h, x1 = -1, y1 = -1; for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) if (m.d[y * m.w + x]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; } return { x0, y0, x1, y1 }; }
  function strokeWidth(m) { const g = dist(m, 0); let s = 0, c = 0; for (let i = 0; i < g.length; i++) if (m.d[i]) { s += g[i]; c++; } return c ? 4 * s / c : 0; }

  // ------------------------------------------------------------------ 3. łączenie liter
  // poziomo: przesuwa B w lewo (dir=-1) albo w prawo, aż zachodzi na A o ov pikseli
  function gapH(A, B) { // najmniejsza odległość w poziomie (B na prawo od A) po wspólnych wierszach
    let best = Infinity;
    for (let y = 0; y < A.h; y++) {
      let ra = -1, lb = -1;
      for (let x = A.w - 1; x >= 0; x--) if (A.d[y * A.w + x]) { ra = x; break; }
      if (ra < 0) continue;
      for (let x = 0; x < B.w; x++) if (B.d[y * B.w + x]) { lb = x; break; }
      if (lb < 0) continue;
      best = Math.min(best, lb - ra - 1);
    }
    return best;
  }
  function gapV(A, B) { // B pod A
    let best = Infinity;
    for (let x = 0; x < A.w; x++) {
      let ba = -1, tb = -1;
      for (let y = A.h - 1; y >= 0; y--) if (A.d[y * A.w + x]) { ba = y; break; }
      if (ba < 0) continue;
      for (let y = 0; y < B.h; y++) if (B.d[y * B.w + x]) { tb = y; break; }
      if (tb < 0) continue;
      best = Math.min(best, tb - ba - 1);
    }
    return best;
  }
  function contact(A, B) { let c = 0; for (let i = 0; i < A.d.length; i++) if (A.d[i] && B.d[i]) c++; return c; }

  // ile wierszy (axis=0) albo kolumn (axis=1) ma wspólne piksele A i B = wysokość/szerokość złącza
  function jointSpan(A, B, axis) {
    const { w, h } = A; let n = 0;
    if (axis === 0) { for (let y = 0; y < h; y++) { for (let x = 0; x < w; x++) { const i = y * w + x; if (A.d[i] && B.d[i]) { n++; break; } } } }
    else { for (let x = 0; x < w; x++) { for (let y = 0; y < h; y++) { const i = y * w + x; if (A.d[i] && B.d[i]) { n++; break; } } } }
    return n;
  }
  // czy złącze A–B jest „mocne”: po usunięciu wszystkiego cieńszego niż jointMM część A i część B
  // wciąż tworzą jeden kawałek. Liczone lokalnie, w oknie wokół B (szybko).
  function jointOK(A, B, ppm, jointMM) {
    const bb = bbox(B), pad = Math.round(2.5 * ppm);
    const x0 = Math.max(0, bb.x0 - pad), y0 = Math.max(0, bb.y0 - pad), x1 = Math.min(A.w - 1, bb.x1 + pad), y1 = Math.min(A.h - 1, bb.y1 + pad);
    const w = x1 - x0 + 1, h = y1 - y0 + 1, U = mk(w, h), onlyA = new Uint8Array(w * h), onlyB = new Uint8Array(w * h);
    let hasA = false;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = (y + y0) * A.w + x + x0, a = A.d[i], b = B.d[i];
      U.d[y * w + x] = a | b; onlyA[y * w + x] = a & (1 - b); onlyB[y * w + x] = b & (1 - a); if (a) hasA = true;
    }
    if (!hasA) return false;
    const op = open(U, jointMM * ppm / 2), L = label(op, true, 1);
    const seenA = new Uint8Array(L.n + 1), seenB = new Uint8Array(L.n + 1);
    for (let i = 0; i < op.d.length; i++) { const l = L.lab[i]; if (!l) continue; if (onlyA[i]) seenA[l] = 1; if (onlyB[i]) seenB[l] = 1; }
    for (let l = 1; l <= L.n; l++) if (seenA[l] && seenB[l]) return true;
    return false;
  }
  // nasuwa B na A w danym kierunku, aż złącze ma ≥ jointMM (jak Mateusz: „żeby wyglądało i trzymało”)
  function slide(A, B, dirx, diry, gap, ppm, opt) {
    const bb = bbox(B), extent = dirx !== 0 ? bb.x1 - bb.x0 + 1 : bb.y1 - bb.y0 + 1;
    const step = Math.max(1, Math.round(0.1 * ppm));
    // litera może wjechać na sąsiada najwyżej na 20% własnej szerokości (i nie więcej niż 3 mm) — dalej robi się nieczytelnie
    const maxExtra = Math.min((opt.maxOverlapMM || 3) * ppm, 0.2 * extent);
    let extra = Math.round(0.1 * ppm), last = B;
    while (extra <= maxExtra) {
      const d = Math.max(gap, 0) + extra;
      const Bs = shift(B, Math.round(dirx * d), Math.round(diry * d));
      last = Bs;
      if (contact(A, Bs) && jointOK(A, Bs, ppm, opt.jointMM || 0.8)) return { B: Bs, ok: true };
      extra += step;
    }
    return { B: last, ok: false };
  }
  // przyciąganie wzdłuż najkrótszego wektora (znak obok napisu, akcent nad literą)
  function attract(A, B, ppm, opt) {
    const gA = dist(A, 1);
    let best = Infinity, bx = 0, by = 0;
    for (let i = 0; i < B.d.length; i++) if (B.d[i] && gA[i] < best) { best = gA[i]; bx = i % B.w; by = (i / B.w) | 0; }
    let ax = bx, ay = by, bd = Infinity; const R = Math.ceil(best) + 2;
    for (let y = Math.max(0, by - R); y < Math.min(A.h, by + R + 1); y++) for (let x = Math.max(0, bx - R); x < Math.min(A.w, bx + R + 1); x++) {
      if (!A.d[y * A.w + x]) continue; const d2 = (x - bx) ** 2 + (y - by) ** 2; if (d2 < bd) { bd = d2; ax = x; ay = y; }
    }
    const L = Math.sqrt(bd) || 1, ux = (ax - bx) / L, uy = (ay - by) / L;
    let extra = 0.1 * ppm, out = B;
    while (extra <= 3 * ppm) {
      out = shift(B, Math.round(ux * (L + extra)), Math.round(uy * (L + extra)));
      if (contact(A, out) && jointOK(A, out, ppm, opt.jointMM || 0.8)) break;
      extra += Math.max(1, 0.1 * ppm);
    }
    return out;
  }

  // profil szczelin wiersz po wierszu (B na prawo od A)
  function gapProfile(A, B) {
    const g = [];
    for (let y = 0; y < A.h; y++) {
      let ra = -1, lb = -1;
      for (let x = A.w - 1; x >= 0; x--) if (A.d[y * A.w + x]) { ra = x; break; }
      if (ra < 0) continue;
      for (let x = 0; x < B.w; x++) if (B.d[y * B.w + x]) { lb = x; break; }
      if (lb < 0) continue;
      g.push(lb - ra - 1);
    }
    return g;
  }
  function closestPair(A, B) {
    const gA = dist(A, 1); let best = Infinity, bx = 0, by = 0;
    for (let i = 0; i < B.d.length; i++) if (B.d[i] && gA[i] < best) { best = gA[i]; bx = i % B.w; by = (i / B.w) | 0; }
    let ax = bx, ay = by, bd = Infinity; const R = Math.ceil(best) + 2;
    for (let y = Math.max(0, by - R); y < Math.min(A.h, by + R + 1); y++) for (let x = Math.max(0, bx - R); x < Math.min(A.w, bx + R + 1); x++) {
      if (!A.d[y * A.w + x]) continue; const d2 = (x - bx) ** 2 + (y - by) ** 2; if (d2 < bd) { bd = d2; ax = x; ay = y; }
    }
    return { ax, ay, bx, by, d: Math.sqrt(bd) };
  }
  // mostek: krótka belka o szerokości wMM między najbliższymi punktami A i B (litery zostają na miejscu)
  function bridge(A, B, ppm, wMM) {
    const p = closestPair(A, B), o = or(A, B), r = wMM * ppm / 2, ext = 0.4 * ppm;
    const L = p.d || 1, ux = (p.bx - p.ax) / L, uy = (p.by - p.ay) / L;
    const x0 = p.ax - ux * ext, y0 = p.ay - uy * ext, x1 = p.bx + ux * ext, y1 = p.by + uy * ext;
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0)) + 1;
    for (let k = 0; k <= n; k++) disk(o, x0 + (x1 - x0) * k / n, y0 + (y1 - y0) * k / n, r, 1);
    return o;
  }

  function joinPieces(m, ppm, opt = {}) {
    const L = label(m, true, 1);
    const minA = CFG.detailMM2 * ppm * ppm;
    let comps = L.comps.slice(1).filter((c) => c.area >= minA);
    const log = { usuniete: 0, doklejone: 0, nasuniete: 0, wiersze: 0 };
    if (comps.length <= 1) return { m, log, ok: true, removed: mk(m.w, m.h) };
    const maxA = Math.max(...comps.map((c) => c.area));
    const small = comps.filter((c) => c.area < CFG.smallFrac * maxA), main = comps.filter((c) => c.area >= CFG.smallFrac * maxA);
    // drobne elementy w linii (≥3) = drobny napis → usuwamy (jak „GENERACJA” w Cuzi)
    const removed = mk(m.w, m.h), attachList = [], used = new Set(), usedMain = new Set();
    const hMed = small.length ? small.map((c) => c.y1 - c.y0 + 1).sort((a, b) => a - b)[small.length >> 1] : 0;
    for (const c of small) {
      if (used.has(c.id)) continue;
      const cl = [c]; used.add(c.id); let grow = true;
      while (grow) {
        grow = false;
        for (const d of small) {
          if (used.has(d.id)) continue;
          if (cl.some((e) => Math.abs(e.cy - d.cy) < 0.6 * hMed && (Math.max(e.x0, d.x0) - Math.min(e.x1, d.x1)) < 1.2 * hMed)) { cl.push(d); used.add(d.id); grow = true; }
        }
      }
      if (cl.length >= CFG.smallTextMin && !opt.keepSmall) {
        // szerokie litery tego samego napisu (M, W) potrafią przekroczyć próg 6% — dołączamy je do napisu,
        // jeśli stoją w tej samej linii, tuż obok i są podobnej wysokości
        const hc = cl.map((e) => e.y1 - e.y0 + 1).sort((a, b) => a - b)[cl.length >> 1];
        let grow2 = true;
        while (grow2) {
          grow2 = false;
          for (const d of main) {
            if (usedMain.has(d.id) || d.area === maxA || (d.y1 - d.y0 + 1) > 1.35 * hc) continue;
            if (cl.some((e) => Math.abs(e.cy - d.cy) < 0.6 * hc && (Math.max(e.x0, d.x0) - Math.min(e.x1, d.x1)) < 1.2 * hc)) { cl.push(d); usedMain.add(d.id); grow2 = true; }
          }
        }
        const ids = new Set(cl.map((e) => e.id));
        for (let i = 0; i < m.d.length; i++) if (ids.has(L.lab[i])) removed.d[i] = 1;
        log.usuniete += cl.length;
      } else attachList.push(...cl);
    }
    const mainLeft = main.filter((c) => !usedMain.has(c.id));
    const hs = mainLeft.map((c) => c.y1 - c.y0 + 1).sort((a, b) => a - b), hMain = hs[hs.length >> 1];
    const anchors = mainLeft.filter((c) => (c.y1 - c.y0 + 1) > 1.6 * hMain);
    // niskie elementy (akcent nad ó, kropka, kreska) nie są literami: nie przesuwamy nimi napisu, tylko doklejamy je na końcu
    const marks = mainLeft.filter((c) => !anchors.includes(c) && (c.y1 - c.y0 + 1) < 0.45 * hMain);
    const letters = mainLeft.filter((c) => !anchors.includes(c) && !marks.includes(c));
    const rows = [];
    for (const c of letters.slice().sort((a, b) => a.cy - b.cy)) {
      const r = rows.find((r) => Math.abs(r.cy - c.cy) < 0.5 * hMain);
      if (r) { r.items.push(c); r.cy = r.items.reduce((s, e) => s + e.cy, 0) / r.items.length; } else rows.push({ cy: c.cy, items: [c] });
    }
    log.wiersze = rows.length;
    const sopt = { jointMM: opt.jointMM || 0.8, maxOverlapMM: 3 };
    // 1) w wierszu: każda kolejna litera dosuwa się do już złożonej części, reszta jedzie razem z nią
    const blocks = [];
    for (const r of rows) {
      r.items.sort((a, b) => a.cx - b.cx);
      let acc = extract(m, L, r.items[0].id), cum = 0;
      for (let k = 1; k < r.items.length; k++) {
        let B = shift(extract(m, L, r.items[k].id), cum, 0);
        if (contact(acc, B) && jointOK(acc, B, ppm, sopt.jointMM)) { acc = or(acc, B); continue; }
        const prof = gapProfile(acc, B), gap = prof.length ? Math.min(...prof) : Infinity;
        const closeFrac = prof.length ? prof.filter((g) => g <= gap + 0.25 * ppm).length / prof.length : 0;
        if (Number.isFinite(gap) && closeFrac >= 0.3 && gap <= 3 * ppm && !opt.noBridges) {
          // krawędzie równoległe (np. FILO): nasunięcie zalałoby prześwit — dajemy mostek
          acc = bridge(acc, B, ppm, sopt.jointMM + 0.2); log.mostki = (log.mostki || 0) + 1; continue;
        }
        if (Number.isFinite(gap)) {
          const before = bbox(B).x0;
          const S = slide(acc, B, -1, 0, gap, ppm, sopt);
          if (!S.ok) log.slabe = (log.slabe || 0) + 1;
          cum += bbox(S.B).x0 - before; B = S.B;
        } else B = attract(acc, B, ppm, sopt);
        log.nasuniete++;
        acc = or(acc, B);
      }
      blocks.push(acc);
    }
    // 2) wiersze łączymy pionowo: dolny podjeżdża do górnego
    let body = blocks.length ? blocks[0] : null;
    for (let k = 1; k < blocks.length; k++) {
      let B = blocks[k];
      if (!(contact(body, B) && jointOK(body, B, ppm, sopt.jointMM))) {
        const gap = gapV(body, B);
        if (Number.isFinite(gap)) { const S = slide(body, B, 0, -1, gap, ppm, sopt); if (!S.ok) log.slabe = (log.slabe || 0) + 1; B = S.B; }
        else B = attract(body, B, ppm, sopt);
        log.nasuniete++;
      }
      body = or(body, B);
    }
    // 3) kotwice (znak obok napisu) i pojedyncze drobne elementy (akcent) — przyciąganie
    const rest = anchors.map((c) => extract(m, L, c.id)).concat(marks.map((c) => extract(m, L, c.id)), attachList.map((c) => extract(m, L, c.id)));
    if (!body) body = rest.shift();
    for (const B of rest.sort((a, b) => count(b) - count(a))) {
      if (contact(body, B)) { body = or(body, B); continue; }
      // znak/tekst: przesuwamy mniejszy element do większego
      if (count(B) > count(body)) body = or(B, attract(B, body, ppm, sopt)); else body = or(body, attract(body, B, ppm, sopt));
      log.doklejone++;
    }
    const nAfter = label(body, true, 1).comps.slice(1).filter((c) => c.area >= minA).length;
    return { m: body, log, ok: nAfter === 1, removed };
  }

  // ------------------------------------------------------------------ 4. wypełnienie konturów
  function fillAll(m) { return or(m, holesOf(m)); }
  function fillEvenOdd(m) {   // rysunek z samych linii: co drugi obszar staje się metalem
    const { w, h } = m, depth = new Int32Array(w * h).fill(1e9);
    const dq = new Int32Array(w * h * 2); let head = w * h, tail = w * h;
    const push = (i, d, front) => { if (d < depth[i]) { depth[i] = d; if (front) dq[--head] = i; else dq[tail++] = i; } };
    for (let x = 0; x < w; x++) { push(x, 0, 0); push((h - 1) * w + x, 0, 0); }
    for (let y = 0; y < h; y++) { push(y * w, 0, 0); push(y * w + w - 1, 0, 0); }
    while (head < tail) {
      const p = dq[head++], x = p % w, y = (p / w) | 0, d = depth[p];
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const X = x + dx, Y = y + dy; if (X < 0 || Y < 0 || X >= w || Y >= h) continue;
        const q = Y * w + X, cost = (m.d[p] === 1 && m.d[q] === 0) ? 1 : 0;   // wyjście z linii = przekroczenie
        if (d + cost < depth[q]) { depth[q] = d + cost; if (cost) dq[tail++] = q; else dq[--head] = q; }
      }
    }
    const o = clone(m);
    for (let i = 0; i < o.d.length; i++) if (!m.d[i] && depth[i] % 2 === 1) o.d[i] = 1;
    return o;
  }

  // ------------------------------------------------------------------ 5. połączenia
  function weakest(m, ppm) {
    // skanujemy od cienkich do grubych i zatrzymujemy się na PIERWSZYM progu, przy którym wzór się rozpada
    // (nie wyszukiwanie binarne: przy dużych progach znikają całe gałęzie i test znów „przechodzi”)
    const test = (T) => {
      const op = open(m, T * ppm / 2), L = label(op, true, 1);
      if (L.n === 0) return false;
      let tot = 0, mx = 0; for (let i = 1; i <= L.n; i++) { tot += L.comps[i].area; mx = Math.max(mx, L.comps[i].area); }
      return mx / tot >= 0.95;
    };
    let pass = 0;
    for (let T = 0.1; T <= 1.2001; T += 0.1) { if (test(T)) pass = T; else break; }
    if (pass >= 1.2 - 1e-6) return 1.2;              // ≥ 1,2 mm — z zapasem
    const T2 = pass + 0.05;
    if (test(T2)) pass = T2;
    if (pass === 0 && test(0.05)) pass = 0.05;
    return +pass.toFixed(2);
  }
  function necks(m, widthMM, ppm) {
    const op = open(m, widthMM * ppm / 2), thin = andNot(m, op);
    const Lo = label(op, true, 1), Lt = label(thin, true, 1);
    const sig = new Uint8Array(Lo.n + 1);
    for (let i = 1; i <= Lo.n; i++) sig[i] = Lo.comps[i].area >= 2 * ppm * ppm ? 1 : 0;
    const touch = Array.from({ length: Lt.n + 1 }, () => new Set());
    const { w, h } = m;
    for (let i = 0; i < thin.d.length; i++) {
      if (!thin.d[i]) continue; const x = i % w, y = (i / w) | 0, t = Lt.lab[i];
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
        const X = x + dx, Y = y + dy; if (X < 0 || Y < 0 || X >= w || Y >= h) continue;
        const l = Lo.lab[Y * w + X]; if (l && sig[l]) touch[t].add(l);
      }
    }
    // schodek na krawędzi leży cały przy bryle; prawdziwy ogonek (cienka kreska) w większości od niej odstaje
    const gOp = dist(op, 1), far = new Int32Array(Lt.n + 1);
    for (let i = 0; i < thin.d.length; i++) if (thin.d[i] && gOp[i] > 2.5) far[Lt.lab[i]]++;
    const nk = mk(w, h), tl = mk(w, h);
    for (let i = 0; i < thin.d.length; i++) {
      if (!thin.d[i]) continue; const t = Lt.lab[i];
      if (touch[t].size >= 2) nk.d[i] = 1; else if (far[t] >= 0.3 * Lt.comps[t].area) tl.d[i] = 1;
    }
    return { necks: nk, tails: tl };
  }
  function fix(m, ppm) {
    const a = necks(m, CFG.bridgeOkMM, ppm), b = necks(m, CFG.minLineMM, ppm);
    let out = m;
    if (count(a.necks)) out = or(out, dilate(a.necks, CFG.bridgeOkMM * ppm / 2));
    if (count(b.tails)) out = or(out, dilate(b.tails, CFG.minLineMM * ppm / 2));
    const hl = holesOf(out), ho = open(hl, CFG.minGapMM * ppm / 2);
    out = or(out, andNot(hl, ho));
    const x = mk(m.w, m.h); for (let i = 0; i < x.d.length; i++) x.d[i] = out.d[i] ^ m.d[i];
    const xo = open(x, 0.1 * ppm);
    return { m: out, change: +(100 * count(xo) / Math.max(1, count(m))).toFixed(1), weak: a.necks };
  }

  // ------------------------------------------------------------------ 6. mocowanie
  function disk(m, cx, cy, r, v) { for (let y = Math.max(0, Math.floor(cy - r)); y <= Math.min(m.h - 1, Math.ceil(cy + r)); y++) for (let x = Math.max(0, Math.floor(cx - r)); x <= Math.min(m.w - 1, Math.ceil(cx + r)); x++) if ((x - cx) ** 2 + (y - cy) ** 2 <= r * r) m.d[y * m.w + x] = v; }
  function mount(m, mode, ppm) {
    const rh = CFG.eyeHoleMM / 2 * ppm, ro = rh + CFG.eyeWallMM * ppm, ov = CFG.eyeOverlapMM * ppm;
    const o = clone(m); let sx = 0, sy = 0, n = 0;
    let bl = Infinity, br = Infinity, L = null, R = null;
    for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) if (m.d[y * m.w + x]) {
      sx += x; sy += y; n++;
      if (x + y < bl) { bl = x + y; L = [x, y]; } if (-x + y < br) { br = -x + y; R = [x, y]; }
    }
    const cx = sx / n, cy = sy / n, rings = [], info = { tryb: mode };
    if (mode === "dwa") {
      const d = (ro - ov) / Math.SQRT2;
      rings.push([L[0] - d, L[1] - d], [R[0] + d, R[1] - d]);
    } else {
      let top = null, band = 1.5 * ppm;
      while (!top) { for (let y = 0; y < m.h && !top; y++) for (let x = Math.max(0, Math.round(cx - band)); x <= Math.min(m.w - 1, Math.round(cx + band)); x++) if (m.d[y * m.w + x]) { top = [x, y]; break; } band += ppm; }
      rings.push(mode === "srodek" ? [top[0], top[1] - (ro - ov)] : [top[0], top[1] + ro - 0.2 * ppm]);
      // v0.4: bez „przechyłu” — Mateusz: wisiory na jednym oczku wiszą prosto, a wyliczenie ze środka ciężkości myliło klientów
    }
    for (const [x, y] of rings) disk(o, x, y, ro, 1);
    for (const [x, y] of rings) disk(o, x, y, rh, 0);
    info.oczka = rings;
    return { m: o, info };
  }

  // ------------------------------------------------------------------ 7. kontury i DXF
  function contours(m, ppm) {
    // marching squares na lekko wygładzonej masce → gładkie kontury (mm)
    const { w, h } = m, f = new Float32Array(w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let s = 0, c = 0; for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) { const X = x + dx, Y = y + dy; if (X >= 0 && Y >= 0 && X < w && Y < h) { const wt = (dx === 0 && dy === 0) ? 4 : (dx === 0 || dy === 0 ? 2 : 1); s += m.d[Y * w + X] * wt; c += wt; } }
      f[y * w + x] = s / c;
    }
    const segs = new Map(), key = (x, y, o) => (o ? 1 : 0) + 2 * (x + (w + 1) * y);
    const pt = new Map();
    const P = (x, y, o) => { const k = key(x, y, o); if (!pt.has(k)) { let px, py; if (o === 0) { const a = f[y * w + x], b = f[y * w + x + 1]; px = x + (0.5 - a) / (b - a); py = y; } else { const a = f[y * w + x], b = f[(y + 1) * w + x]; px = x; py = y + (0.5 - a) / (b - a); } pt.set(k, [px / ppm, py / ppm]); } return k; };
    const add = (a, b) => { if (!segs.has(a)) segs.set(a, []); if (!segs.has(b)) segs.set(b, []); segs.get(a).push(b); segs.get(b).push(a); };
    for (let y = 0; y < h - 1; y++) for (let x = 0; x < w - 1; x++) {
      const a = f[y * w + x] > 0.5, b = f[y * w + x + 1] > 0.5, c = f[(y + 1) * w + x + 1] > 0.5, d = f[(y + 1) * w + x] > 0.5;
      const idx = (a ? 8 : 0) | (b ? 4 : 0) | (c ? 2 : 0) | (d ? 1 : 0);
      if (idx === 0 || idx === 15) continue;
      const T = () => P(x, y, 0), B = () => P(x, y + 1, 0), Lf = () => P(x, y, 1), Rt = () => P(x + 1, y, 1);
      switch (idx) {
        case 1: case 14: add(Lf(), B()); break;
        case 2: case 13: add(B(), Rt()); break;
        case 3: case 12: add(Lf(), Rt()); break;
        case 4: case 11: add(T(), Rt()); break;
        case 6: case 9: add(T(), B()); break;
        case 7: case 8: add(Lf(), T()); break;
        case 5: add(Lf(), T()); add(B(), Rt()); break;
        case 10: add(T(), Rt()); add(Lf(), B()); break;
      }
    }
    const loops = [], seen = new Set();
    for (const start of segs.keys()) {
      if (seen.has(start)) continue;
      const loop = []; let prev = -1, cur = start;
      while (cur !== undefined && !seen.has(cur)) {
        seen.add(cur); loop.push(pt.get(cur));
        const nb = segs.get(cur).filter((k) => k !== prev && !seen.has(k));
        prev = cur; cur = nb[0];
      }
      if (loop.length > 6) loops.push(rdp(loop, 0.02));
    }
    return loops;
  }
  function rdp(pts, eps) {
    if (pts.length < 4) return pts;
    const keep = new Uint8Array(pts.length); keep[0] = keep[pts.length - 1] = 1;
    const st = [[0, pts.length - 1]];
    while (st.length) {
      const [a, b] = st.pop(); let md = 0, mi = -1;
      const [x1, y1] = pts[a], [x2, y2] = pts[b], L = Math.hypot(x2 - x1, y2 - y1) || 1e-9;
      for (let i = a + 1; i < b; i++) { const d = Math.abs((y2 - y1) * pts[i][0] - (x2 - x1) * pts[i][1] + x2 * y1 - y2 * x1) / L; if (d > md) { md = d; mi = i; } }
      if (md > eps) { keep[mi] = 1; st.push([a, mi], [mi, b]); }
    }
    return pts.filter((_, i) => keep[i]);
  }
  function toDXF(loops, H) {
    const L = ["0", "SECTION", "2", "ENTITIES"];
    for (const lp of loops) {
      L.push("0", "POLYLINE", "8", "0", "66", "1", "70", "1");
      for (const [x, y] of lp) L.push("0", "VERTEX", "8", "0", "10", x.toFixed(4), "20", (H - y).toFixed(4));
      L.push("0", "SEQEND");
    }
    L.push("0", "ENDSEC", "0", "EOF");
    return L.join("\n");
  }

  // ------------------------------------------------------------------ pipeline
  function run(img, opt = {}) {
    const t0 = Date.now();
    const sizeMM = opt.sizeMM || 45, mode = opt.mount || "dwa";
    const ppm = opt.ppm || Math.max(10, Math.min(20, Math.round(1200 / (sizeMM + 2 * CFG.marginMM))));
    const im = inkMap(img, opt);
    const W0 = toWork(im, sizeMM, ppm);
    const res = { ppm, sizeMM, diag: im.diag, red: [], amber: [], info: {} };
    if (!W0) { res.verdict = "RECZNIE"; res.red.push("nie znaleziono wzoru w pliku"); return res; }
    let m = W0.m; res.info.pxNaMmPliku = W0.srcPxPerMM;
    // szum: plamki < 1 mm² wypadają; te od 0,15 mm² w górę raportujemy klientowi (to mogą być kropki albo drobny tekst)
    const L0 = label(m, true, 1), minA = CFG.detailMM2 * ppm * ppm, speckA = CFG.speckReportMM2 * ppm * ppm;
    const clean = mk(m.w, m.h), specks = mk(m.w, m.h);
    for (let i = 0; i < m.d.length; i++) { const l = L0.lab[i]; if (!l) continue; const a = L0.comps[l].area; if (a >= minA) clean.d[i] = 1; else if (a >= speckA) specks.d[i] = 1; }
    let nSpeck = 0; for (let l = 1; l <= L0.n; l++) { const a = L0.comps[l].area; if (a < minA && a >= speckA) nSpeck++; }
    m = clean;
    res.info.pominiete = nSpeck;
    if (nSpeck) res.amber.push(`pominięto ${nSpeck} ${nSpeck === 1 ? "drobny element" : (nSpeck % 10 >= 2 && nSpeck % 10 <= 4 && (nSpeck % 100 < 10 || nSpeck % 100 >= 20)) ? "drobne elementy" : "drobnych elementów"} < ${CFG.detailMM2} mm² — za małe do wycięcia`);
    if (opt.fill === "all") m = fillAll(m); else if (opt.fill === "linie") m = fillEvenOdd(m);
    res.masks = { source: m, removed: specks };
    const nP = label(m, true, 1).comps.slice(1).filter((c) => c.area >= minA).length;
    res.info.kawalki = nP;
    // v0.4: po odsianiu drobnicy nic nie zostało (np. cienkie kreski w małym rozmiarze) — zamiast błędu werdykt „ręcznie”
    if (!nP) { res.info.pusty = true; res.verdict = "RECZNIE"; res.red.push("po odrzuceniu elementów < 1 mm² nic nie zostało — wzór za drobny w tym rozmiarze"); res.ms = Date.now() - t0; return res; }
    let joined = m, jl = null;
    if (nP > 1) {
      const J = joinPieces(m, ppm, opt);
      joined = J.m; jl = J.log; res.masks.removed = or(specks, J.removed); res.info.laczenie = J.log;
      if (!J.ok) res.red.push(`nie udało się połączyć ${nP} elementów — połączy Mateusz`);
      else {
        const parts = [];
        if (jl.nasuniete) parts.push(`nasunięto ${jl.nasuniete} ${jl.nasuniete === 1 ? "element" : "elementy"}`);
        if (jl.mostki) parts.push(`dodano ${jl.mostki} ${jl.mostki === 1 ? "mostek" : "mostki"}`);
        if (jl.doklejone) parts.push(`doklejono ${jl.doklejone}`);
        if (jl.usuniete) parts.push(`usunięto drobny napis (${jl.usuniete} el.)`);
        res.amber.push(`osobne elementy połączone automatycznie: ${parts.join(", ")} — propozycja do akceptacji`);
      }
      // po nasunięciu wzór jest węższy — skalujemy z powrotem do zadanego rozmiaru
      const bb = bbox(joined), cur = Math.max(bb.x1 - bb.x0 + 1, bb.y1 - bb.y0 + 1) / ppm;
      if (Math.abs(cur - sizeMM) > 0.5) {
        const soft = { ink: new Float32Array(joined.w * joined.h), w: joined.w, h: joined.h };
        for (let i = 0; i < joined.d.length; i++) soft.ink[i] = joined.d[i];
        joined = toWork(soft, sizeMM, ppm).m;
      }
    }
    res.masks.joined = joined;
    if (im.diag.typ !== "grafika") res.red.push("to zdjęcie, szkic albo kolorowa ilustracja — automat nie wie, co ma być metalem");
    const ws = res.red.length && nP > 1 ? null : weakest(joined, ppm);
    res.info.najslabszePolaczenie = ws;
    if (ws !== null) {
      if (ws < CFG.bridgeMinMM) res.red.push(`wzór trzyma się na połączeniach ~${ws.toFixed(2)} mm („na nitce”) — ryzyko pęknięcia`);
      else if (ws < CFG.bridgeOkMM) res.amber.push(`najsłabsze połączenie ${ws.toFixed(2)} mm — pogrubione do ${CFG.bridgeOkMM} mm`);
      if (ws > 0 && ws < CFG.bridgeOkMM) res.info.rozmiarBezKorekty = Math.ceil(sizeMM * CFG.bridgeOkMM / ws / 5) * 5;
    }
    const F = fix(joined, ppm);
    res.info.zmiana = F.change; res.masks.weak = F.weak;
    if (F.change > 15) res.amber.push(`korekta zmienia ${F.change}% kształtu — do oceny`);
    const M = mount(F.m, mode, ppm);
    res.info.mocowanie = M.info;
    res.masks.final = M.m;
    const bb = bbox(F.m);
    res.info.wymiaryMM = [+((bb.x1 - bb.x0 + 1) / ppm).toFixed(1), +((bb.y1 - bb.y0 + 1) / ppm).toFixed(1)];
    res.info.bboxPx = [bb.x0, bb.y0, bb.x1, bb.y1];   // obrys wzoru bez oczek, w pikselach maski final
    if (opt.contours !== false) { res.loops = contours(M.m, ppm); res.dxf = toDXF(res.loops, M.m.h / ppm); res.info.kontury = res.loops.length; }
    res.verdict = res.red.length ? "RECZNIE" : res.amber.length ? "AUTO_UWAGI" : "AUTO";
    res.ms = Date.now() - t0;
    return res;
  }

  const api = { CFG, run, inkMap, toWork, joinPieces, weakest, fix, mount, contours, toDXF, label, fillEvenOdd, fillAll, bbox, count };
  if (typeof module !== "undefined" && module.exports) module.exports = api; else root.VandalEngine = api;
})(typeof self !== "undefined" ? self : this);